import React from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  CarFront,
  Phone,
  ArrowRight,
  Users,
  Trophy,
  Handshake,
  Building2,
  ShieldCheck,
  Heart,
} from "lucide-react";
import { StatBar } from "@/components/StatBar";
import { FeaturedVehicleCard } from "@/components/FeaturedVehicleCard";
import { HappyCustomers } from "@/components/HappyCustomers";
import { WhyWorkWithMe } from "@/components/WhyWorkWithMe";
import { useVehicles } from "@/hooks/useVehicles";
import { IMAGES } from "@/config";

const heroTrust = [
  { icon: ShieldCheck, title: "Quality Checked", subtitle: "Used Cars" },
  { icon: Users, title: "Straightforward", subtitle: "Advice" },
  { icon: CalendarDays, title: "Easy Viewing", subtitle: "Enquiries" },
];

const stats = [
  { icon: Trophy, value: "5+", label: "Years of Experience", sub: "In the automotive industry" },
  { icon: Users, value: "600+", label: "Happy Customers", sub: "Still growing, year after year." },
  { icon: Handshake, value: "Personal", label: "Buying Support", sub: "From start to finish" },
  { icon: Building2, value: "Toyota Tokai", label: "Affiliation", sub: "Part of a trusted dealership" },
];

const Home: React.FC = () => {
  const { vehicles } = useVehicles();
  const published = vehicles.filter((vehicle) => vehicle.status === "published");
  const availableVehicles = published.slice(0, 3);

  return (
    <>
      {/* Full-screen hero */}
      <section className="home-hero relative min-h-[calc(100svh-4rem)] overflow-hidden bg-gradient-to-br from-slate-50 via-white to-red-50/30">
        <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-red-100/30 blur-3xl" />
        <div className="container-x relative flex min-h-[calc(100svh-4rem)] flex-col py-4 sm:py-5 lg:py-4">
          <div className="home-hero-grid grid flex-1 items-stretch gap-6 lg:min-h-0 lg:grid-cols-[43%_57%] lg:gap-0">
            <div className="home-hero-copy relative flex flex-col justify-center py-7 lg:pr-12 xl:pr-16 lg:pt-14">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.34em] text-slate-500 sm:text-[11px] lg:absolute lg:left-0 lg:top-0 lg:right-12">
                Quality used cars. Real people. A brighter tomorrow.
              </p>
              <h1 className="mt-4 text-[2.7rem] font-extrabold leading-[0.98] tracking-[-0.045em] text-slate-950 sm:text-6xl lg:text-[clamp(3.2rem,4.2vw,5rem)]">
                Find Your Next
                <br />
                Used Car
                <br />
                <span className="relative inline-block text-red-600">
                  In Cape Town.
                  <svg
                    className="absolute -bottom-3 left-[28%] h-3 w-[62%]"
                    viewBox="0 0 260 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M4 13C74 2 159 2 256 12" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>

              <div className="mt-7 max-w-[630px] space-y-4 text-sm leading-relaxed text-slate-600 sm:text-[15px]">
                <p>
                  A good car becomes part of your life long after you leave the showroom. It&apos;s there
                  for the morning commute, family trips, big milestones and everything in between.
                  That&apos;s why I focus on quality pre-owned vehicles you can feel confident putting your
                  name — and your life — behind.
                </p>
                <p>
                  Tell me what you&apos;re looking for and let me do the searching with you. Whether you
                  already have a make or model in mind or you&apos;re starting with nothing more than a budget,
                  I&apos;ll help you narrow down the right options and find a vehicle worth getting excited
                  about.
                </p>
              </div>

              <div className="home-hero-actions mt-5 flex flex-wrap gap-3">
                <Link
                  to="/vehicles/"
                  className="inline-flex min-w-[230px] items-center justify-center gap-3 rounded-xl bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-[0_10px_22px_-10px_rgba(220,38,38,0.9)] transition-colors hover:bg-red-700"
                  data-testid="hero-explore"
                >
                  <CarFront className="h-5 w-5" /> Explore Used Cars <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/contact/#contact-enquiry"
                  className="inline-flex min-w-[180px] items-center justify-center gap-3 rounded-xl border border-red-500 bg-white px-6 py-3.5 text-sm font-bold text-slate-900 transition-colors hover:bg-red-50"
                  data-testid="hero-call"
                >
                  <Phone className="h-5 w-5 text-red-600" /> Let&apos;s Talk
                </Link>
              </div>

              <div className="home-hero-trust mt-5 grid max-w-[610px] grid-cols-3 gap-3">
                {heroTrust.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="flex min-w-0 items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-red-600 shadow-md ring-1 ring-slate-100">
                        <Icon className="h-5 w-5" strokeWidth={2.2} />
                      </span>
                      <span className="min-w-0 leading-tight">
                        <span className="block text-xs font-extrabold text-slate-950">{item.title}</span>
                        <span className="block text-[11px] text-slate-500">{item.subtitle}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="home-hero-photo relative min-h-[420px] overflow-hidden rounded-2xl bg-slate-200 lg:min-h-0" style={{ clipPath: "inset(0 0 0 5% round 16px)" }}>
              <img
                src={IMAGES.homeHero}
                alt="Simphiwe Fudwana at Toyota Tokai Automark"
                className="absolute inset-0 h-full w-full origin-right scale-[1.045] object-cover object-center"
              />
              <div className="absolute left-[8%] top-5 flex items-center gap-3 rounded-2xl border border-white/80 bg-white/95 py-2 pl-2 pr-4 shadow-[0_12px_32px_rgba(15,23,42,0.18)] backdrop-blur-sm">
                <img
                  src={IMAGES.brandLogo}
                  alt=""
                  className="h-14 w-14 rounded-xl object-contain"
                  width="56"
                  height="56"
                />
                <span className="leading-tight">
                  <strong className="block text-sm font-extrabold text-slate-950">Simphiwe Motors</strong>
                  <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-red-600">Good people. Great journeys.</span>
                </span>
              </div>
            </div>
          </div>

          <div className="home-hero-stats relative z-10 mt-4 shrink-0">
            <StatBar items={stats} />
          </div>
        </div>
      </section>

      {/* Available vehicles */}
      <section className="home-featured" aria-labelledby="available-heading">
        <div className="container-x home-featured-inner">
          <p className="home-featured-eyebrow">Available Vehicles</p>
          <div className="home-featured-heading">
            <div>
              <h2 id="available-heading">Quality Vehicles, Personally Recommended</h2>
              <p className="home-featured-description">Carefully selected, quality checked, and ready for your next adventure.</p>
            </div>
            <Link
              to="/vehicles/"
              className="inline-flex shrink-0 items-center gap-2 text-sm font-bold text-red-600 hover:text-red-700"
              data-testid="home-view-all"
            >
              View All Vehicles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        <div className="home-featured-grid">
          {availableVehicles.map((v) => (
            <FeaturedVehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
        </div>
      </section>

      {/* About preview */}
      <section className="home-about" aria-labelledby="home-about-heading">
        <div className="container-x home-about-inner">
          <div className="home-about-photo">
            <img src="/images/about-portrait-enhanced.png" alt="Simphiwe Fudwana with arms crossed at Toyota Tokai" loading="lazy" />
          </div>
          <div className="home-about-content">
            <div className="home-about-eyebrow">About Simphiwe <span /></div>
            <div className="home-about-title-row">
              <h2 id="home-about-heading">A More Personal Way<br />To Buy <span>Your Next Car</span></h2>
            </div>
            <div className="home-about-copy">
              <p>Buying a car is a big decision, and the right support can make all the difference. I work closely with each customer to understand what they need, answer questions clearly, and make the experience feel simple, comfortable, and well guided from the very beginning.</p>
              <p>With 120+ cars sold each year, I focus on helping people move with confidence through a more personal, one-on-one approach. Whether you already know what you want or need help narrowing down your options, I’m here to help you find the right fit for your lifestyle, priorities, and budget.</p>
            </div>
            <div className="home-about-highlights">
              {[
                { icon: CarFront, title: "120+", label: "Cars Sold Per Year", detail: "Experience that helps you compare your options and choose with confidence." },
                { icon: Users, title: "Personal", label: "One-on-One Guidance", detail: "Your lifestyle, priorities and budget shape every recommendation." },
                { icon: CalendarDays, title: "Easy Viewing", label: "Book viewings with ease", detail: "Take a closer look at your next car at a time arranged around you." },
                { icon: Heart, title: "Customer First", label: "A smoother buying experience", detail: "Support from your first question through handover and the journey beyond." },
              ].map(({ icon: Icon, title, label, detail }, index) => (
                <details className="about-benefit" key={title}>
                  <summary>
                    <Icon className="about-benefit-watermark" aria-hidden="true" />
                    <span className="about-benefit-number" aria-hidden="true">0{index + 1}</span>
                    <span className="about-benefit-orbit"><Icon aria-hidden="true" /></span>
                    <strong>{title}</strong>
                    <span className="about-benefit-label">{label}</span>
                    <span className="about-benefit-description">{[
                      "Real people. Real journeys. Over 120 happy drivers every year.",
                      "A more personal, hands-on approach tailored to your needs.",
                      "Flexible times, straightforward process, and a no-hassle experience.",
                      "Your lifestyle, priorities and budget come first.",
                    ][index]}</span>
                    <span className="about-benefit-tags">{[
                      ["Proven Track Record", "Happy Customers"],
                      ["Tailored to You", "No Pressure"],
                      ["Flexible Scheduling", "Quick & Easy"],
                      ["Your Goals Matter", "Long-Term Support"],
                    ][index].map(tag => <span key={tag}><ShieldCheck aria-hidden="true" />{tag}</span>)}</span>
                  </summary>
                  <p>{detail}</p>
                </details>
              ))}
            </div>
            <div className="home-about-bottom">
              <div className="home-about-actions">
                <Link to="/about/" className="home-about-primary" data-testid="home-read-more">Read More<ArrowRight aria-hidden="true" /></Link>
                <Link to="/contact/" className="home-about-secondary">Get In Touch <ArrowRight aria-hidden="true" /></Link>
              </div>
              <div className="home-about-signature"><span /><div><strong>Simphiwe Fudwana</strong><small>Car Sales Consultant</small></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Happy customers */}
      <WhyWorkWithMe />

      <section className="bg-slate-50/70">
        <HappyCustomers />
      </section>

    </>
  );
};

export default Home;
